/******************************************** STYLING *********************************************/
const s = require('../auth-panel-root.scss');
const mailFailImg = require('./envelope-fail.svg');

/************************************** THIRD-PARTY IMPORTS ***************************************/
import React from 'react';
import DocumentTitle from 'react-document-title';
import cn from 'classnames';
import {inject} from 'mobx-react';
import {FormSubmitHandler, IfFalsy, IfTruthy} from 'mad-utils/lib/react';

/**************************************** PROJECT IMPORTS *****************************************/
import {StoreProps} from '../../../../types/types';
import {translate, SubmitButton} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
interface EmailVerificationFailCardViewProps extends StoreProps {
    email: string;
    sendVerificationEmail: FormSubmitHandler;
    success: boolean;
    isLoading: boolean;
}

/************************************** LOCAL SUBCOMPONENTS ***************************************/
/**
 * Logo sitting about main page heading
 * As of 2017-06-13, contains image of an envelope with an X on the bottom-right
 * Example usage: <EmailVerificationLogo />
 */
const EmailVerificationLogo = () => (
    <div>
        <img src={mailFailImg} className={cn(`h-85`, `mb-10`)} />
    </div>
);

/**
 * Main heading for email-verification-fail screen. Largest font size. Sits below logo.
 * As of 2017-06-13, contained text:
 *     "This verification link is invalid."
 * Eexample usage: <EmailVerificationHeading />
 */
const EmailVerificationHeading = () => (
    <h1 className={cn(s[`input-card-text`], `ci-card-content-width`, `dmt-20--smt-5`)}>
        {translate(`this_verification_link_is_invalid`).rmEndPunc}
    </h1>
);

/**
 * Subheading for email-verification-fail screen. 2nd-largest font size. Sits below heading.
 * As of 2017-06-13, contained text:
 *     "This link may have expired. Tap the button below to resend the email."
 * Eexample usage: <EmailVerificationSubheading />
 */
const EmailVerificationSubheading = () => (
    <h2
        className={cn(
            s[`input-card-text`],
            `ci-card-content-width`,
            `mr-20 ml-20`,
            `dmt-20--smt-5`
        )}
    >
        {translate(`error_verification_expired`).rmEndPunc}
        {`. `}
    </h2>
);

/********************************************* EXPORT *********************************************/
/**
 * View component for card to resend verification link, in cases where provided
 * link was invalid
 */
@inject(`appState`)
export class EmailVerificationFailCardView extends React.Component<
    EmailVerificationFailCardViewProps
> {
    render() {
        const {success} = this.props;
        const pageTitle = `${translate(`title_email_verification`)} - CANImmunize`;

        return (
            <DocumentTitle title={pageTitle}>
                <div className={cn(`h-85p`, s[`input-card`])}>
                    <div className={cn(s[`card-info-section-wrapper`])}>
                        <EmailVerificationLogo />
                        <EmailVerificationHeading />
                        <EmailVerificationSubheading />
                    </div>
                    <div className={cn(`mt-n10`, `ta-center`)}>
                        <IfFalsy test={!!success}>
                            <SubmitButton
                                isLoading={this.props.isLoading}
                                onClick={this.props.sendVerificationEmail}
                                mergeClasses={cn(`mt-25`)}
                            >
                                {translate(`email_verify_resend`)}
                            </SubmitButton>
                        </IfFalsy>
                        <IfTruthy test={!!success}>
                            <SubmitButton
                                disabled={true}
                                style={{backgroundColor: `#65c76b`}}
                                onClick={null}
                                mergeClasses={cn(`mt-25`)}
                            >
                                {translate(`verification_email_sent`)}
                            </SubmitButton>
                        </IfTruthy>
                    </div>
                </div>
            </DocumentTitle>
        );
    }
}

EmailVerificationFailCardView.displayName = `EmailVerificationFailCardView`;
